/**
 * Workflows API client.
 *
 * Mirrors `backend/src/hub/routes/workflows.py` and the pydantic
 * models in `hub/workflow/definition.py` and `hub/workflow/catalog.py`.
 *
 * Conventions:
 *  - All string enums match the backend `NodeKind` / `Direction` /
 *    `NodeCategory` enums character-for-character so the catalog
 *    response is consumable as-is.
 *  - `WorkflowDefinition` is the JSON the editor produces and that the
 *    backend validates against the same pydantic shape.
 */
import { api } from "./api";
import type { Paginated, PageSize } from "./pagination";

// ---------- enums (mirror backend) ----------------------------------------

export type Direction = "inbound" | "internal" | "outbound";

export type NodeCategory =
  | "source"
  | "sink"
  | "transform"
  | "control"
  // ``action`` is the n8n-style category for synchronous external calls
  // whose response feeds the next node (http_request, future: soap/graphql).
  // Distinct from ``sink`` (fire-and-forget enqueue) and from ``lookup``
  // (resolves through the typed Mapping registry).
  | "action"
  | "io_canonical"
  | "lookup"
  | "plugin";

export type NodeKind =
  // source
  | "webhook_source"
  | "sftp_source"
  | "file_watch_source"
  | "db_poll_source"
  | "http_cron_source"
  | "manual_source"
  // ADR-0013 — sub-workflow entry (receives the caller's items)
  | "workflow_input"
  // orchestration triggers (no IngressSource binding — config inline)
  | "schedule_trigger"
  | "webhook_trigger"
  // ADR-0065 T1-4 — entry node of an error workflow (on-failure handler)
  | "error_trigger"
  // ADR-0008 PR2 — source for reactive sub-flows wired from a sink branch
  | "delivery_trigger"
  // ADR-0010 D10 — app triggers (app-kind only)
  | "row_changed"
  | "form_submitted"
  | "button_clicked"
  // sink
  | "http_sink"
  | "file_sink"
  | "connector_sink"
  | "respond_to_webhook"
  // transform
  | "set_fields"
  | "js_code_transform"
  | "sandbox_code"
  // ADR-0069 — declarative Pillow-backed image pipeline
  | "image"
  // control (post-rename: branch→if, split+loop→loop_over_items, router→merge)
  | "filter"
  | "if"
  | "switch"
  | "loop_over_items"
  | "loop"
  | "aggregate"
  | "merge"
  | "wait"
  // ADR-0013 — synchronous sub-workflow call + child return marker
  | "call_workflow"
  | "workflow_output"
  // ADR-0038 P3 — fire an ETL pipeline from a workflow
  | "run_etl_pipeline"
  // action — synchronous request/reply (response feeds downstream)
  | "http_request"
  // ADR-0010 D10 — read/write an app data table (app-kind only)
  | "data_table"
  | "read_write_file"
  | "extract_from_file"
  // postgres integration — grouped Actions (ADR-0011)
  | "postgres.execute_query"
  | "postgres.select"
  | "postgres.insert"
  | "postgres.update"
  | "postgres.delete"
  | "postgres.upsert"
  | "postgres.on_new_event"
  // s3 / object storage (ADR-0069)
  | "s3.upload"
  | "s3.download"
  | "s3.list"
  | "s3.delete"
  | "s3.presign"
  // mysql integration — grouped Actions (ADR-0011); no trigger (no LISTEN/NOTIFY)
  | "mysql.execute_query"
  | "mysql.select"
  | "mysql.insert"
  | "mysql.update"
  | "mysql.delete"
  | "mysql.upsert"
  // sqlserver integration — grouped Actions (ADR-0011); no trigger
  | "sqlserver.execute_query"
  | "sqlserver.select"
  | "sqlserver.insert"
  | "sqlserver.update"
  | "sqlserver.delete"
  | "sqlserver.upsert"
  // io_canonical
  | "read_canonical"
  | "write_canonical"
  | "emit_event"
  // lookup
  | "call_mapping"
  // plugin
  | "plugin_call";

// ---------- catalog --------------------------------------------------------

export interface CatalogNode {
  kind: NodeKind;
  category: NodeCategory;
  label: string;
  description: string;
  /** Which workflow kinds (canonical / orchestration) this node is
   *  available in. Drives the palette filter. Defaults to BOTH on the
   *  backend; the palette respects it. */
  kinds?: WorkflowKind[];
  /**
   * JSON Schema (pydantic-generated) for the node's `config` field.
   * The editor uses this to render the per-node form panel and validate
   * before save.
   */
  config_schema: Record<string, unknown>;
  /** ADR-0011 Integration model. Connector/app Actions carry the
   *  integration id + operation so the palette groups them under one
   *  entry (n8n model). Regular nodes leave these null. */
  integration?: string | null;
  operation?: string | null;
  is_trigger?: boolean;
  /** Which per-item/batch execution modes this node kind allows + the
   *  default. Drives the Settings-tab "Execution mode" select (hidden
   *  when only one mode is supported). Mirrors NodeSpecOut. */
  supported_modes?: ExecutionMode[];
  default_mode?: ExecutionMode;
}

/** How the executor invokes a node's handler (n8n "run once per item" vs
 *  "run once with all items"). Mirrors the backend ExecutionMode enum. */
export type ExecutionMode = "per_item" | "once_on_batch";

/** A connector grouping several Action/Trigger node kinds (ADR-0011). */
export interface CatalogIntegration {
  id: string;
  label: string;
  description: string;
  icon?: string | null;
}

export interface CatalogResponse {
  nodes: CatalogNode[];
  categories: NodeCategory[];
  directions: Direction[];
  integrations?: CatalogIntegration[];
}

// ---------- workflow definition (DAG payload) -----------------------------

export interface CanonicalRef {
  model: string;
  version: number;
}

export interface NodePosition {
  x: number;
  y: number;
}

export interface WorkflowNode {
  id: string;
  kind: NodeKind;
  label?: string | null;
  config: Record<string, unknown>;
  position: NodePosition;
  /** n8n "Execute Once" Setting: run the node once with only the first
   *  input item. Default false. (ADR-0009 Track 2a.) */
  execute_once?: boolean;
  /** n8n-style node Settings (ADR-0009 Track 2b/2c/2d). */
  notes?: string | null;
  always_output_data?: boolean;
  retry_on_fail?: boolean;
  max_tries?: number;
  retry_wait_ms?: number;
  on_error?: "stop" | "continue" | "continue_error_output";
  /** Per-node execution mode. Null/undefined = the catalog spec's
   *  default_mode (the backend validator backfills it on save). */
  execution_mode?: ExecutionMode | null;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  /** Named output handle on the source node (e.g. "true"/"false" for
   *  branch, case ids for switch). Null/undefined = single-output node. */
  source_handle?: string | null;
}

/** Top-level workflow specialisation. See specs/workflow-palette.md.
 *  Defaults to 'canonical' so existing clients keep working untouched. */
export type WorkflowKind = "canonical" | "orchestration" | "app";

export interface WorkflowDefinition {
  name: string;
  /** Optional in API responses for back-compat; defaults to 'canonical'. */
  kind?: WorkflowKind;
  /** Null when kind === 'orchestration'. */
  direction?: Direction | null;
  /** Null when kind === 'orchestration'. */
  canonical?: CanonicalRef | null;
  /** REQUIRED when kind === 'app' (binds the workflow to an Apps app);
   *  MUST be null/absent otherwise. Mirrors the backend WorkflowDefinition
   *  validator (ADR-0010 D10). */
  app_id?: string | null;
  description?: string | null;
  /** Optional global on-failure handler (ADR-0065 T1-4). When a PRODUCTION run
   *  fails, the durable engine spawns this workflow with the failure context.
   *  Versioned with the definition; the loop guard caps the chain at depth 1. */
  error_workflow_id?: string | null;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

// ---------- workflow + versions -------------------------------------------

export interface WorkflowSummary {
  id: string;
  name: string;
  kind?: WorkflowKind;
  direction: Direction | null;
  canonical_model: string | null;
  canonical_version: number | null;
  /** ADR-0010 D10 — set when kind='app'. */
  app_id?: string | null;
  description: string | null;
  enabled: boolean;
  /** Free-form organizational tags. Always a list (never null). */
  tags: string[];
  active_version_id: string | null;
  active_version_number: number | null;
  versions_count: number;
  created_by: string;
  created_at: string;
  updated_at: string | null;
  // ADR-0008 PR3b3 — sub-flow marker. Hidden from the main workflows
  // list by default; the editor pulls them via include_subflows=true
  // when rendering the unified canvas.
  is_subflow?: boolean;
  parent_workflow_id?: string | null;
  parent_branch_id?: string | null;
}


/** Request body for POST /workflows/{id}/sub-flow-for-branch (PR3b3). */
export interface CreateSubflowForBranchRequest {
  sink_node_id: string;
  branch_id: string;
  name?: string | null;
}

export interface WorkflowVersionSummary {
  id: string;
  workflow_id: string;
  version: number;
  notes: string | null;
  created_by: string;
  created_at: string;
}

export interface WorkflowVersionDetail extends WorkflowVersionSummary {
  definition: WorkflowDefinition;
}

export interface WorkflowDetail extends WorkflowSummary {
  active_version: WorkflowVersionDetail | null;
  recent_versions: WorkflowVersionSummary[];
}

export interface WorkflowCreate {
  name: string;
  /** Defaults to 'canonical' server-side when omitted. */
  kind?: WorkflowKind;
  /** Required iff kind === 'canonical', omit for orchestration. */
  direction?: Direction;
  /** Required iff kind === 'canonical'. */
  canonical_model?: string;
  /** Required iff kind === 'canonical'. */
  canonical_version?: number;
  /** Required iff kind === 'app' (ADR-0010 D10). */
  app_id?: string;
  description?: string | null;
  tags?: string[];
}

export interface WorkflowUpdate {
  name?: string;
  description?: string | null;
  enabled?: boolean;
  /** ``undefined`` = leave unchanged; ``[]`` = clear all tags. */
  tags?: string[];
}

export interface WorkflowVersionCreate {
  definition: WorkflowDefinition;
  notes?: string | null;
  activate?: boolean;
}

// ---------- per-node recent inputs / single-node run ---------------------


export interface RecentInputEntry {
  execution_id: string;
  started_at: string;
  status: string;
  payload: unknown;
}


export interface RecentInputsResponse {
  entries: RecentInputEntry[];
  /** Hint shown to the user (e.g. "intermediate captures not wired"). */
  note: string | null;
}


export interface RunSingleNodeRequest {
  node_id: string;
  node_kind: NodeKind;
  config?: Record<string, unknown>;
  label?: string | null;
  input_payload?: unknown;
}


export interface RunSingleNodeResponse {
  status: "succeeded" | "failed" | "skipped";
  output_payload: unknown;
  error: string | null;
  log_outputs: Record<string, unknown>;
  duration_ms: number;
}


// ---------- send-test-payload (multi-ingress) -----------------------------


export interface WorkflowIngressSourceRef {
  ingress_source_id: string;
  ingress_source_name: string;
  ingress_source_type: string;
  node_id: string;
  node_kind: string;
}


export interface SendTestPayloadResponse {
  message_id: string;
  deduplicated: boolean;
  workflow_execution_id: string | null;
}


// ---------- test-run ------------------------------------------------------

export interface TestRunRequest {
  /** Optional. When omitted, the backend tries to synthesise a payload
   *  via the trigger strategy of the chosen source (cron / scheduler
   *  records, etc.). For manual_source you still need to pass one. */
  payload?: unknown;
  /** Optional override — if absent the active version is used. */
  definition?: WorkflowDefinition;
  version_id?: string;
  /** Which source/trigger node we're running from. Required when the
   *  DAG has more than one source. Single-source DAGs auto-resolve. */
  trigger_node_id?: string;
  /** n8n-style step debug: halt after this node id. The pinned
   *  execution can later be passed as ``resume_from_execution_id``. */
  stop_after_node_id?: string;
  /** Resume from a previously pinned execution. When set, ``payload`` is
   *  ignored — the parent's initial_payload is replayed verbatim. */
  resume_from_execution_id?: string;
  /** ADR-0065 T1-4 — opt-in: also fire the workflow's configured error
   *  workflow when this test-run FAILS (off by default, n8n parity). Lets you
   *  test the on-failure handler from the editor without a real trigger. */
  fire_error_workflow?: boolean;
}


// ---------- trigger strategies ------------------------------------------
//
// The editor's Run dialog asks the backend "what should the input
// look like for this trigger?" via POST /trigger-strategies. The
// returned ``kind`` drives which UI the dialog renders.

export type TriggerStrategyKind =
  | "auto_payload"   // backend synthesised — show preview + Run
  | "manual_input"   // user types JSON
  | "external_hit"   // user sends a real HTTP call to a URL
  | "poll_once"      // hint only in v1 (fall back to manual JSON)
  | "unknown";

export interface TriggerStrategy {
  kind: TriggerStrategyKind;
  node_id: string;
  node_kind: string;
  label: string;
  description: string;
  /** Populated when kind === "auto_payload". */
  payload?: unknown;
  /** Populated when kind === "external_hit". The literal token
   *  ``<host>`` is left in place — the UI substitutes
   *  ``window.location.host`` before display. */
  external_url?: string;
  curl_example?: string;
  /** Free-text hint for poll_once / unknown. */
  hint?: string;
}

export interface TriggerStrategiesResponse {
  strategies: TriggerStrategy[];
}


// ---------- test-webhook (Listen-for-test-event) -------------------------


export interface TestWebhookMintResponse {
  /** The unguessable handle for this listen session. UI keeps it
   *  alive across long-poll rounds and passes it to cancel/await. */
  token: string;
  /** Server-relative URL the user POSTs to. UI prepends
   *  ``window.location.origin`` to show a copy-paste URL. */
  capture_path: string;
  /** Server-relative URL the UI long-polls. (Built into the helper
   *  ``workflowsApi.awaitTestWebhook`` — not consumed directly.) */
  await_path: string;
  /** Outer TTL on the slot in seconds. Beyond this the backend gives
   *  up; the UI should stop polling and surface a "session expired"
   *  hint. */
  expires_in_seconds: number;
}


export interface TestWebhookCaptureResponse {
  method: string;
  path: string;
  query: Record<string, unknown>;
  headers: Record<string, string>;
  body: unknown;
}

export interface TestRunNodeTrace {
  node_id: string;
  node_kind: NodeKind;
  status: "succeeded" | "failed" | "skipped";
  input?: unknown;
  output?: unknown;
  duration_ms: number;
  error: string | null;
  log_outputs: Record<string, unknown>;
  /** Non-zero for nodes that ran inside a SPLIT iteration. */
  iteration: number;
}

export interface TestRunResponse {
  status: "succeeded" | "failed";
  final_payload: unknown;
  node_traces: TestRunNodeTrace[];
  error: string | null;
  /** ID of the workflow_executions row written during this run. Used
   *  by the editor's step-debug flow to pin / resume the execution. */
  execution_id: string | null;
  /** ADR-0065 T1-4 — True when this failed test-run ALSO spawned the
   *  configured error workflow (because ``fire_error_workflow`` was set). */
  error_workflow_fired?: boolean;
}

/** ADR-0068 F4 — the ƒx live-preview result (server-evaluated). */
export interface PreviewExpressionResponse {
  ok: boolean;
  /** The resolved value (native type preserved for a single `{{ expr }}`). */
  value: unknown;
  /** A compact one-line rendering for the `= …` line. */
  display: string | null;
  /** The evaluation error, when `ok` is false. */
  error: string | null;
  /** True when the template references an upstream node (`$('Label')`) whose
   *  output isn't available yet — the editor shows an "esegui i nodi
   *  precedenti" hint instead of the raw error. */
  needs_upstream?: boolean;
}

export interface PreviewImageResponse {
  ok: boolean;
  /** The rendered result as a PNG data URL (`data:image/png;base64,…`). */
  data_url: string | null;
  width: number | null;
  height: number | null;
  /** The pipeline/config error, when `ok` is false. */
  error: string | null;
}

// ---------- HTTP --------------------------------------------------------------

export const workflowsApi = {
  catalog: () => api.get<CatalogResponse>("/workflows/catalog"),

  /** ADR-0068 F4 — evaluate ONE `{{ }}` field template server-side (the
   *  engine's QuickJS) for the ƒx field's live `= value` preview. */
  previewExpression: (body: {
    template: string;
    sample?: Record<string, unknown>;
    items?: Record<string, unknown>[];
    node_refs?: Record<string, unknown>;
  }) =>
    api.post<PreviewExpressionResponse>(
      "/workflows/preview-expression",
      body,
    ),

  /** ADR-0069 S2 — render the `image` node pipeline on a sample image
   *  server-side (Pillow) → a PNG data URL, for the inspector's live preview. */
  previewImage: (body: {
    config: Record<string, unknown>;
    sample_image: string;
    sample_data?: Record<string, unknown>;
  }) =>
    api.post<PreviewImageResponse>("/workflows/preview-image", body),

  list: (params?: {
    /** Filter by top-level specialisation. WorkflowsPage uses
     *  ``canonical`` (default historical behaviour); AutomationsPage
     *  uses ``orchestration``. Omit to include both. */
    kind?: WorkflowKind;
    direction?: Direction;
    enabled?: boolean;
    /** ADR-0010 D10 — scope app-kind workflows to one app. */
    app_id?: string;
    /** Repeatable: AND semantics — rows must contain all given tags. */
    tag?: string[];
    /** ADR-0008 PR3b3 — opt-in to include auto-created sub-flows.
     *  Default false: the main workflows list doesn't show them. */
    include_subflows?: boolean;
    /** ADR-0065 T1-4 — only workflows whose active definition has an
     *  error_trigger node (the "error workflow" picker). */
    has_error_trigger?: boolean;
    page?: number;
    page_size?: PageSize;
  }) => api.get<Paginated<WorkflowSummary>>("/workflows", { query: params }),

  get: (id: string) =>
    api.get<WorkflowDetail>(`/workflows/${id}`),

  create: (body: WorkflowCreate) =>
    api.post<WorkflowSummary>("/workflows", body),

  update: (id: string, body: WorkflowUpdate) =>
    api.patch<WorkflowSummary>(`/workflows/${id}`, body),

  delete: (id: string) =>
    api.delete<void>(`/workflows/${id}`),

  listVersions: (id: string) =>
    api.get<WorkflowVersionSummary[]>(`/workflows/${id}/versions`),

  getVersion: (id: string, versionId: string) =>
    api.get<WorkflowVersionDetail>(
      `/workflows/${id}/versions/${versionId}`,
    ),

  createVersion: (id: string, body: WorkflowVersionCreate) =>
    api.post<WorkflowVersionDetail>(
      `/workflows/${id}/versions`,
      body,
    ),

  activateVersion: (id: string, versionId: string) =>
    api.post<WorkflowSummary>(
      `/workflows/${id}/versions/${versionId}/activate`,
    ),

  /** Discard a version — draft-first cleanup. The API refuses the ACTIVE
   *  version and any version with runs (history), so this only ever removes
   *  an inert draft. */
  deleteVersion: (id: string, versionId: string) =>
    api.delete<void>(`/workflows/${id}/versions/${versionId}`),

  /** Fork any workflow (with an active version) into a new editable
   *  one. Replaces the old template-only ``clone`` endpoint. Name
   *  defaults to ``"<source> (copy)"`` if omitted; conflicts return
   *  409 so the user picks a different name explicitly. */
  duplicate: (id: string, body?: { name?: string; description?: string | null }) =>
    api.post<WorkflowDetail>(`/workflows/${id}/duplicate`, body ?? {}),

  /** ADR-0008 PR3b3 — auto-create a hidden sub-flow workflow for a
   *  sink branch. Called by the editor when the operator clicks `+`
   *  on a sink branch handle that has no linked_workflow_id yet.
   *  Returns the new workflow summary (with is_subflow=true,
   *  parent_workflow_id set, active v1 containing a delivery_trigger
   *  source). The caller is responsible for writing the returned id
   *  back to the parent sink's ``branches[*].linked_workflow_id``. */
  createSubflowForBranch: (
    parentId: string,
    body: CreateSubflowForBranchRequest,
  ) =>
    api.post<WorkflowSummary>(
      `/workflows/${parentId}/sub-flow-for-branch`,
      body,
    ),

  /** Last N inputs seen by a node — feeds the node editor's INPUT pane.
   *  Source nodes return real history; other nodes return an empty list
   *  with an explanatory note (see backend docstring). */
  recentNodeInputs: (
    id: string,
    nodeId: string,
    params?: { limit?: number },
  ) =>
    api.get<RecentInputsResponse>(
      `/workflows/${id}/nodes/${encodeURIComponent(nodeId)}/recent-inputs`,
      { query: params },
    ),

  /** Execute a single node with a given input. Non-durable,
   *  doesn't write to workflow_executions — for editor interactive run. */
  runSingleNode: (id: string, body: RunSingleNodeRequest) =>
    api.post<RunSingleNodeResponse>(
      `/workflows/${id}/run-single-node`,
      body,
    ),

  /** Ingress sources referenced by source nodes in the active version.
   *  Drives the "Send test payload" picker. */
  ingressBindings: (id: string) =>
    api.get<WorkflowIngressSourceRef[]>(`/workflows/${id}/ingress-bindings`),

  /** Inject a payload as if received from a bound ingress source.
   *  Goes through the real intake worker path — execution shows up in
   *  /workflow-runs after a few seconds. */
  sendTestPayload: (
    id: string,
    body: {
      ingress_source_id: string;
      payload: unknown;
      content_type?: string | null;
    },
  ) =>
    api.post<SendTestPayloadResponse>(
      `/workflows/${id}/send-test-payload`,
      body,
    ),

  testRun: (id: string, body: TestRunRequest) =>
    api.post<TestRunResponse>(`/workflows/${id}/test-run`, body),

  /** "How should I run this workflow?" — one strategy per source node. */
  triggerStrategies: (id: string, body: { definition?: WorkflowDefinition }) =>
    api.post<TriggerStrategiesResponse>(
      `/workflows/${id}/trigger-strategies`,
      body,
    ),

  /** Mint a one-shot test-webhook URL. The UI shows Listening… until
   *  ``awaitTestWebhook`` resolves with the captured request. */
  mintTestWebhook: (id: string) =>
    api.post<TestWebhookMintResponse>(`/workflows/${id}/test-webhook`, {}),

  /** Long-poll for the test webhook hit. Timeout is in seconds; the
   *  endpoint caps it at 60 server-side, so the UI loops to fake a
   *  longer wait. The token is now path-shaped (e.g. ``demo/echo``)
   *  so it rides as a query param — putting it in the URL path
   *  would clash with the ``/await`` suffix. */
  awaitTestWebhook: (id: string, wh_token: string, timeoutSeconds = 30) =>
    api.get<TestWebhookCaptureResponse>(
      `/workflows/${id}/test-webhook/await`,
      { query: { wh_token, timeout: timeoutSeconds } },
    ),

  /** Stop button — drop the slot proactively. */
  cancelTestWebhook: (id: string, wh_token: string) =>
    api.delete<void>(`/workflows/${id}/test-webhook`, { query: { wh_token } }),

  /** Publish the test-run result back to the parked curl caller.
   *  The capture endpoint at /api/webhooks/test/<path> is awaiting
   *  this so it can return the workflow output to the operator's
   *  terminal — exactly what a production hit would see. */
  respondTestWebhook: (
    id: string,
    wh_token: string,
    response: { status_code?: number; headers?: Record<string, string>; body: unknown },
  ) =>
    api.post<void>(
      `/workflows/${id}/test-webhook/respond`,
      {
        status_code: response.status_code ?? 200,
        headers: response.headers ?? {},
        body: response.body,
      },
      { query: { wh_token } },
    ),

  listSamples: (id: string) =>
    api.get<WorkflowSample[]>(`/workflows/${id}/samples`),

  upsertSample: (
    id: string,
    body: { name: string; payload: unknown; description?: string | null },
  ) => api.post<WorkflowSample>(`/workflows/${id}/samples`, body),

  deleteSample: (id: string, sampleId: string) =>
    api.delete<void>(`/workflows/${id}/samples/${sampleId}`),
};

export interface WorkflowSample {
  id: string;
  workflow_id: string;
  name: string;
  description: string | null;
  payload: unknown;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}


// ---------- workflow executions (read-only history) -----------------------

export interface NodeExecutionSummary {
  id: string;
  node_id: string;
  node_kind: NodeKind;
  status: "pending" | "running" | "succeeded" | "failed" | "skipped";
  iteration: number;
  duration_ms: number | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
  // Inline payloads — populated for runs after migration 0028.
  // Older rows stay null; the UI shows a "payload not captured" hint.
  input?: unknown;
  output?: unknown;
  log_outputs?: Record<string, unknown> | null;
}

/** Aggregated state of the outbox deliveries spawned by one execution.
 *  All zero → the execution had no sinks (UI renders "—"). */
export interface DeliverySummary {
  pending: number;
  in_flight: number;
  succeeded: number;
  failed: number;
  dead_letter: number;
}


export interface ExecutionSummary {
  id: string;
  workflow_id: string;
  workflow_name: string;
  workflow_version_id: string;
  workflow_version: number | null;
  direction: Direction | "";
  message_id: string | null;
  /** The FULL runtime vocabulary — mirrors the backend
   *  ck_workflow_executions_trigger CHECK (see RUNTIME_TRIGGERS in
   *  tests/workflow/test_execution_trigger_constraint.py + the
   *  sub_workflow/workflow_node spawn sites in handlers.py). */
  trigger:
    | "webhook"
    | "cron"
    | "manual"
    | "event"
    | "replay"
    | "delivery_hook"
    | "row_changed"
    | "form_submitted"
    | "button_clicked"
    | "sub_workflow"
    | "workflow_node";
  status: "running" | "succeeded" | "failed" | "cancelled" | "waiting" | "resuming";
  started_at: string;
  finished_at: string | null;
  error: string | null;
  node_count: number;
  failed_node_count: number;
  delivery_summary: DeliverySummary;
  parent_execution_id: string | null;
}

export interface ChildExecutionRef {
  id: string;
  workflow_id: string;
  workflow_name: string;
  direction: Direction | "";
  trigger: ExecutionSummary["trigger"];
  status: ExecutionSummary["status"];
  started_at: string;
  finished_at: string | null;
}

export interface ExecutionDetail extends ExecutionSummary {
  nodes: NodeExecutionSummary[];
  children: ChildExecutionRef[];
  // ADR-0066 S2 — true when this run is parked on an APPROVAL wait (show
  // Approve/Reject). A timer wait resumes on its own → false.
  awaiting_approval?: boolean;
}

export const workflowExecutionsApi = {
  list: (params?: {
    workflow_id?: string;
    status?: ExecutionSummary["status"];
    trigger?: ExecutionSummary["trigger"];
    page?: number;
    page_size?: PageSize;
  }) => {
    const qs = params
      ? "?" +
        Object.entries(params)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";
    return api.get<Paginated<ExecutionSummary>>(`/workflow-executions${qs}`);
  },

  get: (id: string) =>
    api.get<ExecutionDetail>(`/workflow-executions/${id}`),

  /**
   * One node's freshest persisted trace (live-I/O slice). During a sync
   * test-run each node's trace is committed the moment the node finishes
   * (BEFORE its node_finished SSE fires), so the node drawer fetches the
   * just-finished node here instead of pulling the whole execution detail.
   * 404 = "not yet" (the caller treats it as pending, not an error).
   */
  nodeTrace: (executionId: string, nodeId: string) =>
    api.get<NodeExecutionSummary>(
      `/workflow-executions/${executionId}/nodes/${encodeURIComponent(nodeId)}`,
    ),

  retry: (id: string) =>
    api.post<{ new_execution_id: string | null; dbos_workflow_id: string | null }>(
      `/workflow-executions/${id}/retry`,
    ),

  cancel: (id: string) =>
    api.post<{ cancelled: boolean }>(`/workflow-executions/${id}/cancel`),

  // ADR-0066 S2 — approve/reject a run parked on an approval wait.
  resume: (id: string, decision: "approved" | "rejected") =>
    api.post<{ resumed: boolean }>(`/workflow-executions/${id}/resume`, { decision }),

  /**
   * Download a node's binary slot (input or output) as a file. Fetches
   * with the auth header (a plain `<a download>` can't carry the bearer),
   * then triggers a browser download from a blob URL. Resolves inline +
   * external binaries server-side. ``suggestedName`` is a fallback when
   * the response carries no Content-Disposition filename.
   */
  async downloadNodeBlob(
    executionId: string,
    args: {
      node: string;
      slot: string;
      item?: number;
      io?: "input" | "output";
      suggestedName?: string;
    },
  ): Promise<void> {
    const { useAuthStore } = await import("./auth");
    const params = new URLSearchParams({
      node: args.node,
      slot: args.slot,
      item: String(args.item ?? 0),
      io: args.io ?? "output",
    });
    const token = useAuthStore.getState().token;
    const res = await fetch(
      `/api/admin/workflow-executions/${executionId}/blob?${params.toString()}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) {
      throw new Error(`Download failed: ${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    triggerBlobDownload(
      blob,
      res.headers
        .get("content-disposition")
        ?.match(/filename="([^"]+)"/)?.[1] ??
        args.suggestedName ??
        `${args.slot}.bin`,
    );
  },
};


// ---------------------------------------------------------------------------
// DB schema introspection — powers the Postgres node's auto-suggest pickers
// (schema / table / columns) in the editor. Mirrors
// backend/src/hub/routes/db_introspect.py.
// ---------------------------------------------------------------------------

export interface DbColumnInfo {
  name: string;
  type: string;
}

export interface DbIntrospectResult {
  items: string[];
  columns: DbColumnInfo[];
}

export function dbIntrospect(args: {
  credentialId: string;
  level: "schemas" | "tables" | "columns";
  schema?: string;
  table?: string;
}): Promise<DbIntrospectResult> {
  const q: Record<string, string> = {
    credential_id: args.credentialId,
    level: args.level,
  };
  if (args.schema) q.schema = args.schema;
  if (args.table) q.table = args.table;
  return api.get<DbIntrospectResult>("/db-introspect", { query: q });
}


/** Save a Blob to disk via a transient object-URL anchor click. Shared
 *  by the node-blob download and the inline (client-side) binary case. */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


/** Decode a base64 string to a Blob with the given MIME type — used to
 *  download an *inline* binary slot fully client-side (no network). */
export function base64ToBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime || "application/octet-stream" });
}
